import {
  FilterPerformanceMetrics,
  FilterPerformanceMetricsV1,
  MetricsReporter
} from 'src/context/NewFilterContext/utils/performanceTypes'

declare global {
  interface Window {
    posthog: any
  }
}

/**
 * PostHog reporter for filter performance metrics
 */
export const posthogPerformanceReporter: MetricsReporter = async (metrics: FilterPerformanceMetrics) => {
  // Import PostHog dynamically if not on window
  let posthog: any

  if (typeof window !== 'undefined') {
    // Try window.posthog first
    posthog = window.posthog

    // If not on window, try importing the module
    if (!posthog) {
      try {
        const posthogModule = await import('posthog-js')
        posthog = posthogModule.default
      } catch (e) {
        console.warn('[PostHog] Could not import posthog-js module')
      }
    }
  }

  // Check if PostHog is available and has capture method
  if (!posthog || typeof posthog.capture !== 'function') {
    console.warn('[PostHog] PostHog not initialized or capture method not available')
    return
  }

  try {
    // Log in development
    if (process.env.NODE_ENV === 'development') {
      console.log('[PostHog] Sending filter_performance event')
    }

    // Send main filter performance event with all relevant data
    posthog.capture('filter_performance', {
      // Core timing metrics
      load_time_ms: metrics.loadTime_ms,
      t1_ms: metrics.t1_ms,
      t2_ms: metrics.t2_ms,

      // Cache performance
      cache_hit: metrics.cache_hit,
      data_was_stale: metrics.data_was_stale,

      // Data info
      total_filters_count: metrics.total_filters_count,

      // User information
      user_email: (metrics as any).user_email || null,
      user_org: (metrics as any).user_org || null,
      user_name: (metrics as any).user_name || null,
      access_level: (metrics as any).access_level || null,

      // Context
      page_path: metrics.page_path,
      session_id: metrics.session_id,

      // Performance category for easier filtering in PostHog
      performance_category: getPerformanceCategory(metrics.loadTime_ms),
      cache_effectiveness: getCacheEffectiveness(metrics)
    })

    if (process.env.NODE_ENV === 'development') {
      console.debug('[PostHog] Filter performance metrics sent:', {
        loadTime: metrics.loadTime_ms,
        category: getPerformanceCategory(metrics.loadTime_ms),
        cacheHit: metrics.cache_hit
      })
    }

  } catch (error) {
    console.error('[PostHog] Error reporting metrics:', error)
  }
}

/**
 * Get performance category based on load time
 */
function getPerformanceCategory(loadTimeMs: number): string {
  if (loadTimeMs < 100) return 'instant'
  if (loadTimeMs < 300) return 'fast'
  if (loadTimeMs < 1000) return 'moderate'
  if (loadTimeMs < 3000) return 'slow'
  return 'very_slow'
}

/**
 * Get cache effectiveness rating
 */
function getCacheEffectiveness(metrics: FilterPerformanceMetrics): string {
  if (!metrics.cache_hit) return 'no_cache'
  if (metrics.cache_hit && !metrics.data_was_stale) return 'perfect'
  if (metrics.data_was_stale) return 'stale'
  return 'unknown'
}

/**
 * PostHog reporter V1 for comprehensive filter performance metrics
 * Tracks both new API (chainFiltersV1) and old API (getAllFiltersData + getUserAssignedBNamesData) separately
 */
export const posthogPerformanceReporterV1 = async (metrics: FilterPerformanceMetricsV1) => {
  let posthog: any

  if (typeof window !== 'undefined') {
    posthog = window.posthog
    if (!posthog) {
      try {
        const posthogModule = await import('posthog-js')
        posthog = posthogModule.default
      } catch (e) {
        console.warn('[PostHog] Could not import posthog-js module')
      }
    }
  }

  if (!posthog || typeof posthog.capture !== 'function') {
    console.warn('[PostHog] PostHog not initialized or capture method not available')
    return
  }

  try {
    const eventData = {
      // General timing metrics
      load_time_ms: metrics.loadTime_ms,
      t1_ms: metrics.t1_ms,
      t2_ms: metrics.t2_ms,

      // API categorization
      api_type: metrics.api_type,
      is_internal_user: metrics.is_internal_user,

      // API-specific T2 metrics (actual API fetch time)
      // Note: T1 (cache) is general/shared - use t1_ms above
      new_api_t2_ms: metrics.new_api_t2_ms,
      old_api_t2_ms: metrics.old_api_t2_ms,

      // Firebase config state
      internal_enabled: metrics.internal_enabled,
      external_enabled: metrics.external_enabled,

      // Cache performance
      cache_hit: metrics.cache_hit,
      data_was_stale: metrics.data_was_stale,

      // Data info
      total_filters_count: metrics.total_filters_count,

      // User information
      user_email: metrics.user_email || null,
      user_org: metrics.user_org || null,
      user_name: metrics.user_name || null,
      access_level: metrics.access_level || null,

      // Context
      page_path: metrics.page_path,
      session_id: metrics.session_id,

      // Performance category for easier filtering in PostHog
      performance_category: getPerformanceCategory(metrics.loadTime_ms),
      cache_effectiveness: getCacheEffectiveness(metrics)
    }

    // Send to PostHog
    posthog.capture('filter_performance_v1', eventData)

  } catch (error) {
    console.error('[PostHog] Error reporting V1 metrics:', error)
  }
}

export default posthogPerformanceReporter